import React, { useEffect, useMemo, useState } from 'react';
import CustomSelect from '../components/CustomSelect';

export default function UsersView({
  usersWorkspace,
  isLoading,
  isSaving,
  saveError,
  onCreateUser,
  onUpdateUser,
  onRemoveUser,
  terminology
}) {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [draft, setDraft] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const users = useMemo(() => {
    if (!usersWorkspace) {
      return [];
    }

    return usersWorkspace.users;
  }, [usersWorkspace]);

  useEffect(() => {
    if (!usersWorkspace) {
      setSelectedUserId('');
      setDraft(null);
      setIsEditDialogOpen(false);
      setIsCreateDialogOpen(false);
      setCreateDraft(null);
      return;
    }
  }, [usersWorkspace]);

  // Sync draft when selected user or workspace changes while dialog is open
  useEffect(() => {
    if (!usersWorkspace || !isEditDialogOpen || !selectedUserId) {
      return;
    }

    const selectedUser = usersWorkspace.users.find((user) => user.id === selectedUserId);
    if (selectedUser) {
      setDraft(buildUserDraft(selectedUser));
    } else {
      setIsEditDialogOpen(false);
      setDraft(null);
    }
  }, [usersWorkspace, selectedUserId, isEditDialogOpen]);

  useEffect(() => {
    if (!usersWorkspace) {
      setCreateDraft(null);
      return;
    }

    if (isCreateDialogOpen) {
      setCreateDraft(createEmptyDraft());
    }
  }, [usersWorkspace, isCreateDialogOpen]);

  useEffect(() => {
    if (!isCreateDialogOpen && !isEditDialogOpen) {
      return undefined;
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape' && !isSaving) {
        if (isDeleteDialogOpen) {
          setIsDeleteDialogOpen(false);
          return;
        }
        if (isCreateDialogOpen) {
          setIsCreateDialogOpen(false);
          setCreateDraft(createEmptyDraft());
        }
        if (isEditDialogOpen) {
          setIsEditDialogOpen(false);
          setDraft(null);
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isCreateDialogOpen, isEditDialogOpen, isDeleteDialogOpen, isSaving]);

  if (isLoading) {
    return (
      <section className="panel roster-loading">
        <div className="loading-card">
          <div className="spinner-ring" />
          <h1>Loading users</h1>
          <p>Preparing the user management workspace.</p>
        </div>
      </section>
    );
  }

  if (!usersWorkspace) {
    return (
      <section className="panel roster-loading">
        <div className="loading-card">
          <h1>Unable to load users</h1>
          <p>You may not have permission to manage users, or the workspace is still loading.</p>
        </div>
      </section>
    );
  }

  const selectedUser = usersWorkspace.users.find((user) => user.id === selectedUserId) ?? null;

  function handleDraftChange(field, value) {
    setDraft((currentDraft) => ({
      ...currentDraft,
      [field]: value
    }));
  }

  function handleDraftPermissionChange(permKey, checked) {
    setDraft((currentDraft) => ({
      ...currentDraft,
      permissions: {
        ...currentDraft.permissions,
        [permKey]: checked
      }
    }));
  }

  function handleDraftProjectToggle(projectId) {
    setDraft((currentDraft) => {
      const current = currentDraft.projectIds;
      const next = current.includes(projectId)
        ? current.filter((id) => id !== projectId)
        : [...current, projectId];
      return { ...currentDraft, projectIds: next };
    });
  }

  function handleCreateDraftChange(field, value) {
    setCreateDraft((currentDraft) => ({
      ...currentDraft,
      [field]: value
    }));
  }

  function handleCreateDraftPermissionChange(permKey, checked) {
    setCreateDraft((currentDraft) => ({
      ...currentDraft,
      permissions: {
        ...currentDraft.permissions,
        [permKey]: checked
      }
    }));
  }

  function handleCreateDraftProjectToggle(projectId) {
    setCreateDraft((currentDraft) => {
      const current = currentDraft.projectIds;
      const next = current.includes(projectId)
        ? current.filter((id) => id !== projectId)
        : [...current, projectId];
      return { ...currentDraft, projectIds: next };
    });
  }

  function handleReset() {
    if (!selectedUser) {
      return;
    }

    setDraft(buildUserDraft(selectedUser));
  }

  function handleRowClick(userId) {
    setSelectedUserId(userId);
    const user = usersWorkspace.users.find((u) => u.id === userId);
    if (user) {
      setDraft(buildUserDraft(user));
      setIsEditDialogOpen(true);
    }
  }

  function handleCloseEditDialog() {
    if (isSaving) {
      return;
    }

    setIsEditDialogOpen(false);
    setDraft(null);
  }

  function handleCreateClick() {
    if (isSaving) {
      return;
    }

    setIsCreateDialogOpen(true);
  }

  function handleCloseCreateDialog() {
    if (isSaving) {
      return;
    }

    setIsCreateDialogOpen(false);
    setCreateDraft(createEmptyDraft());
  }

  async function handleSave(event) {
    event.preventDefault();

    if (!draft || !selectedUser) {
      return;
    }

    const payload = {
      firstName: draft.firstName,
      lastName: draft.lastName,
      email: draft.email,
      role: draft.role,
      permissions: draft.permissions,
      allProjects: draft.allProjects,
      projectIds: draft.projectIds,
      isActive: draft.isActive
    };

    const result = await onUpdateUser(selectedUser.id, payload);
    if (result) {
      setIsEditDialogOpen(false);
      setDraft(null);
    }
  }

  async function handleCreateSubmit(event) {
    event.preventDefault();

    if (!createDraft) {
      return;
    }

    const payload = {
      firstName: createDraft.firstName,
      lastName: createDraft.lastName,
      email: createDraft.email,
      tempPassword: createDraft.password,
      role: createDraft.role,
      permissions: createDraft.permissions,
      allProjects: createDraft.allProjects,
      projectIds: createDraft.projectIds
    };

    const updatedWorkspace = await onCreateUser(payload);

    if (!updatedWorkspace) {
      return;
    }

    const createdUser = updatedWorkspace.users.find(
      (user) => user.email === payload.email
    );

    setIsCreateDialogOpen(false);
    setCreateDraft(createEmptyDraft());
    setSelectedUserId(createdUser?.id ?? '');
  }

  function handleOpenDeleteDialog() {
    if (!selectedUser || isSaving) {
      return;
    }
    setDeleteError('');
    setIsDeleteDialogOpen(true);
  }

  function handleCloseDeleteDialog() {
    if (isSaving) {
      return;
    }
    setIsDeleteDialogOpen(false);
    setDeleteError('');
  }

  async function handleConfirmDelete() {
    if (!selectedUser || isSaving) {
      return;
    }

    setDeleteError('');

    try {
      const updatedWorkspace = await onRemoveUser(selectedUser.id);

      if (updatedWorkspace) {
        setIsDeleteDialogOpen(false);
        setIsEditDialogOpen(false);
        setSelectedUserId('');
        setDraft(null);
      }
    } catch (requestError) {
      setDeleteError(requestError.message || 'Unable to delete the user.');
    }
  }

  function formatLastLogin(lastLoginAt) {
    if (!lastLoginAt) {
      return 'Never';
    }

    try {
      const date = new Date(lastLoginAt);
      return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return 'Unknown';
    }
  }

  function renderPermissionsSection(currentDraft, onPermChange) {
    if (currentDraft.role === 'admin') {
      return null;
    }

    const permissionDefs = [
      { key: 'edit_factors', label: 'Edit factors' },
      { key: 'edit_students', label: `Add/edit/delete ${(terminology?.students ?? 'students').toLowerCase()}` },
      { key: 'edit_classes', label: `Add/edit/delete ${(terminology?.classes ?? 'classes').toLowerCase()}` },
      { key: 'edit_grades', label: 'Add/edit/delete grades' },
      { key: 'run_planner', label: 'Run the planner' }
    ];

    return (
      <div className="users-permissions-section">
        <span className="auth-field"><span>Permissions</span></span>
        {permissionDefs.map((perm) => (
          <label key={perm.key} className="users-checkbox-row">
            <input
              type="checkbox"
              checked={!!currentDraft.permissions[perm.key]}
              onChange={(event) => onPermChange(perm.key, event.target.checked)}
            />
            <span>{perm.label}</span>
          </label>
        ))}
      </div>
    );
  }

  function renderProjectAccessSection(currentDraft, onAllProjectsChange, onProjectToggle) {
    if (currentDraft.role === 'admin') {
      return null;
    }

    const projects = usersWorkspace.projects ?? [];

    return (
      <div className="users-project-access-section">
        <span className="auth-field"><span>Project access</span></span>
        <label className="users-checkbox-row">
          <input
            type="checkbox"
            checked={!!currentDraft.allProjects}
            onChange={(event) => onAllProjectsChange(event.target.checked)}
          />
          <span>Access all projects</span>
        </label>
        {!currentDraft.allProjects ? (
          <div className="users-project-list">
            {projects.map((project) => (
              <label key={project.id} className="users-checkbox-row">
                <input
                  type="checkbox"
                  checked={currentDraft.projectIds.includes(project.id)}
                  onChange={() => onProjectToggle(project.id)}
                />
                <span>{project.name}</span>
              </label>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="users-screen">
      <section className="hero-card roster-hero">
        <div className="hero-copy">
          <div className="eyebrow-row">
            <span className="tone-pill accent">Users</span>
            <span className="eyebrow-copy">
              {users.length} user{users.length === 1 ? '' : 's'} registered
            </span>
          </div>
          <h1>User Management</h1>
          <p>
            Manage who can access SmartSort and control what each user is allowed to do across your projects.
          </p>
        </div>

        <div className="hero-actions">
          <div className="roster-stat-block">
            <span className="metric-label">Users</span>
            <strong>{users.length}</strong>
          </div>
          <div className="roster-stat-block">
            <span className="metric-label">Admins</span>
            <strong>{users.filter((u) => u.role === 'admin').length}</strong>
          </div>
          <div className="roster-stat-block">
            <span className="metric-label">Projects</span>
            <strong>{(usersWorkspace.projects ?? []).length}</strong>
          </div>
        </div>
        <div className="roster-action-bar">
          <button
            type="button"
            className="primary-button compact-button"
            onClick={handleCreateClick}
            disabled={isSaving}
          >
            + Add user
          </button>
        </div>
      </section>

      <div style={{ height: '1rem' }} />

      <section className="panel">
        <div className="panel-head">
          <div>
            <span className="panel-kicker">User directory</span>
            <h2>All users</h2>
          </div>
          <span className="toolbar-meta">{users.length} user{users.length === 1 ? '' : 's'}</span>
        </div>

        <div className="table-wrap roster-list-wrap">
          <table className="data-grid users-grid-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last Login</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={5}>No users found</td>
                </tr>
              ) : null}
              {users.map((user) => (
                <tr
                  key={user.id}
                  className={user.id === selectedUserId ? 'is-selected' : ''}
                  onClick={() => handleRowClick(user.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <div className="list-cell">
                      <strong>{user.displayName}</strong>
                      <span>{user.firstName} {user.lastName}</span>
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`tone-pill role-pill ${user.role === 'admin' ? 'purple' : 'accent'}`}>
                      {user.role === 'admin' ? 'Admin' : 'User'}
                    </span>
                  </td>
                  <td>
                    <span className={`tone-pill role-pill ${user.isActive ? 'good' : 'neutral'}`}>
                      {user.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{formatLastLogin(user.lastLoginAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Edit user dialog */}
      {isEditDialogOpen && draft && selectedUser ? (
        <div className="dialog-overlay" onClick={handleCloseEditDialog}>
          <div
            className="dialog-panel panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-user-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="panel-head compact">
              <div>
                <span className="panel-kicker">Edit user</span>
                <h2 id="edit-user-title">{selectedUser.displayName}</h2>
              </div>
              <button
                type="button"
                className="icon-button classes-dialog-close"
                onClick={handleCloseEditDialog}
                aria-label="Close edit user dialog"
                disabled={isSaving}
              >
                &times;
              </button>
            </div>

            {saveError ? <div className="auth-error">{saveError}</div> : null}

            <form className="roster-form users-editor-form" onSubmit={handleSave}>
              <div className="users-form-grid">
                <label className="auth-field">
                  <span>First name</span>
                  <input
                    type="text"
                    value={draft.firstName}
                    onChange={(event) => handleDraftChange('firstName', event.target.value)}
                    placeholder=""
                    autoFocus
                  />
                </label>

                <label className="auth-field">
                  <span>Last name</span>
                  <input
                    type="text"
                    value={draft.lastName}
                    onChange={(event) => handleDraftChange('lastName', event.target.value)}
                    placeholder=""
                  />
                </label>

                <label className="auth-field">
                  <span>Email</span>
                  <input
                    type="email"
                    value={draft.email}
                    onChange={(event) => handleDraftChange('email', event.target.value)}
                    placeholder=""
                  />
                </label>

                <label className="auth-field">
                  <span>Role</span>
                  <CustomSelect
                    value={draft.role}
                    options={[{ value: 'admin', label: 'Admin' }, { value: 'user', label: 'User' }]}
                    onChange={(val) => handleDraftChange('role', val)}
                  />
                </label>

                <label className="auth-field">
                  <span>Status</span>
                  <CustomSelect
                    value={draft.isActive ? 'active' : 'inactive'}
                    options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
                    onChange={(val) => handleDraftChange('isActive', val === 'active')}
                  />
                </label>
              </div>

              {renderPermissionsSection(draft, handleDraftPermissionChange)}

              {renderProjectAccessSection(
                draft,
                (checked) => handleDraftChange('allProjects', checked),
                handleDraftProjectToggle
              )}

              <div className="roster-form-actions">
                <button
                  type="button"
                  className="utility-button danger-text"
                  onClick={handleOpenDeleteDialog}
                  disabled={isSaving}
                >
                  Delete
                </button>
                <button type="button" className="utility-button" onClick={handleReset} disabled={isSaving}>
                  Reset
                </button>
                <button type="submit" className="primary-button" disabled={isSaving}>
                  {isSaving ? <span className="button-spinner" aria-hidden="true" /> : null}
                  {isSaving ? 'Saving user' : 'Save user'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {/* Create user dialog */}
      {isCreateDialogOpen ? (
        <div className="dialog-overlay" onClick={handleCloseCreateDialog}>
          <div
            className="dialog-panel panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-user-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="panel-head compact">
              <div>
                <span className="panel-kicker">New user</span>
                <h2 id="create-user-title">Add user</h2>
              </div>
              <button
                type="button"
                className="icon-button classes-dialog-close"
                onClick={handleCloseCreateDialog}
                aria-label="Close add user dialog"
                disabled={isSaving}
              >
                &times;
              </button>
            </div>

            {saveError ? <div className="auth-error" style={{ marginBottom: 12 }}>{saveError}</div> : null}

            <form className="roster-form users-editor-form" onSubmit={handleCreateSubmit}>
              <div className="users-form-grid">
                <label className="auth-field">
                  <span>First name</span>
                  <input
                    type="text"
                    value={createDraft?.firstName ?? ''}
                    onChange={(event) => handleCreateDraftChange('firstName', event.target.value)}
                    placeholder=""
                    autoFocus
                  />
                </label>

                <label className="auth-field">
                  <span>Last name</span>
                  <input
                    type="text"
                    value={createDraft?.lastName ?? ''}
                    onChange={(event) => handleCreateDraftChange('lastName', event.target.value)}
                    placeholder=""
                  />
                </label>

                <label className="auth-field">
                  <span>Email</span>
                  <input
                    type="email"
                    value={createDraft?.email ?? ''}
                    onChange={(event) => handleCreateDraftChange('email', event.target.value)}
                    placeholder=""
                  />
                </label>

                <label className="auth-field">
                  <span>Temporary password</span>
                  <input
                    type="password"
                    value={createDraft?.password ?? ''}
                    onChange={(event) => handleCreateDraftChange('password', event.target.value)}
                    placeholder=""
                  />
                </label>

                <label className="auth-field">
                  <span>Role</span>
                  <CustomSelect
                    value={createDraft?.role ?? 'user'}
                    options={[{ value: 'admin', label: 'Admin' }, { value: 'user', label: 'User' }]}
                    onChange={(val) => handleCreateDraftChange('role', val)}
                  />
                </label>
              </div>

              {createDraft ? renderPermissionsSection(createDraft, handleCreateDraftPermissionChange) : null}

              {createDraft ? renderProjectAccessSection(
                createDraft,
                (checked) => handleCreateDraftChange('allProjects', checked),
                handleCreateDraftProjectToggle
              ) : null}

              <div className="roster-form-actions">
                <button
                  type="button"
                  className="utility-button"
                  onClick={handleCloseCreateDialog}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-button" disabled={isSaving}>
                  {isSaving ? <span className="button-spinner" aria-hidden="true" /> : null}
                  {isSaving ? 'Saving user' : 'Create user'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {/* Delete user dialog */}
      {isDeleteDialogOpen && selectedUser ? (
        <div className="dialog-overlay" onClick={handleCloseDeleteDialog}>
          <div
            className="dialog-panel panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-user-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="panel-head compact">
              <div>
                <span className="panel-kicker">Confirm deletion</span>
                <h2 id="delete-user-title">Delete {selectedUser.displayName}?</h2>
              </div>
              <button
                type="button"
                className="icon-button classes-dialog-close"
                onClick={handleCloseDeleteDialog}
                aria-label="Close delete confirmation"
                disabled={isSaving}
              >
                &times;
              </button>
            </div>

            {deleteError ? <div className="auth-error">{deleteError}</div> : null}

            <div className="delete-confirm-body">
              <p>
                This will permanently remove <strong>{selectedUser.displayName}</strong> ({selectedUser.email}) and revoke their access to all projects.
              </p>

              <div className="roster-form-actions">
                <button
                  type="button"
                  className="utility-button"
                  onClick={handleCloseDeleteDialog}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="primary-button danger-button"
                  onClick={handleConfirmDelete}
                  disabled={isSaving}
                >
                  {isSaving ? <span className="button-spinner" aria-hidden="true" /> : null}
                  {isSaving ? 'Deleting' : 'Delete user'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function buildUserDraft(user) {
  return {
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    email: user.email ?? '',
    role: user.role ?? 'user',
    isActive: user.isActive ?? true,
    permissions: { ...(user.permissions ?? {}) },
    allProjects: user.allProjects ?? false,
    projectIds: [...(user.projectIds ?? [])]
  };
}

function createEmptyDraft() {
  return {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'user',
    permissions: {
      edit_factors: false,
      edit_students: false,
      edit_classes: false,
      edit_grades: false,
      run_planner: false
    },
    allProjects: true,
    projectIds: []
  };
}
